import type { ComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppBadgeColor } from '@shared/ui/badges'

import { useListsStore } from '@shared/model'

/** Status → badge tone. Labels come from `lists.promocode.status`. */
export const PROMOCODE_STATUS_COLORS: Record<string, AppBadgeColor> = {
  ACTIVE: 'emerald',
  DISABLED: 'slate',
}

interface PromocodeOptionsReturn {
  statusMap: ComputedRef<Record<string, string>>
  statusOptions: ComputedRef<AppSelectOption[]>
  getStatusLabel: (status: string) => string
}

export const usePromocodeOptions = (): PromocodeOptionsReturn => {
  const listsStore = useListsStore()

  const statusMap = computed<Record<string, string>>(() => listsStore.getLists?.promocode?.status ?? {})

  const statusOptions = computed<AppSelectOption[]>(() =>
    Object.entries(statusMap.value).map(([value, text]) => ({ value, text })))

  const getStatusLabel = (status: string): string => statusMap.value[status] ?? status

  return { statusMap, statusOptions, getStatusLabel }
}

/** 0% is a real discount; only a blank or non-numeric value means "no discount". */
export const toDiscount = (value: string | null | undefined): number | null => {
  const trimmed = value?.trim() ?? ''
  const discount = Number(trimmed)

  return trimmed !== '' && Number.isFinite(discount) ? discount : null
}

export const toDiscountOptions = (discounts: string[]): AppSelectOption<number>[] =>
  discounts
    .map(toDiscount)
    .filter((discount): discount is number => discount !== null)
    .map(discount => ({ value: discount, text: `${discount}%` }))
